import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { insuranceProducts } from '../mock/insuranceData';
import { Button } from '../components/ui/button';
import { submitLead } from '../lib/leadSubmit';
import { useSeo } from '../lib/useSeo';
import StructuredData from '../components/StructuredData';
import {
  Heart, Car, Bike, Shield, ShieldCheck, BadgeCheck, CalendarClock, Stethoscope, Wallet, TrendingUp,
  HeartPulse, Sparkles, Wrench, Cog, Percent, Users, CalendarDays, Gift, Award, CalendarCheck,
  Banknote, Siren, RotateCcw, ChevronDown, Phone, ArrowRight, CheckCircle2, Star
} from 'lucide-react';

const iconMap = {
  Heart, Car, Bike, Shield, ShieldCheck, BadgeCheck, CalendarClock, Stethoscope, Wallet, TrendingUp,
  HeartPulse, Sparkles, Wrench, Cog, Percent, Users, CalendarDays, Gift, Award, CalendarCheck,
  Banknote, Siren, RotateCcw,
};

const accentClasses = {
  emerald: { text: 'text-emerald-600', bg: 'bg-emerald-600', bgLight: 'bg-emerald-50', border: 'border-emerald-200', hover: 'hover:bg-emerald-700', ring: 'ring-emerald-100', chip: 'bg-emerald-100 text-emerald-700' },
  blue: { text: 'text-blue-600', bg: 'bg-blue-600', bgLight: 'bg-blue-50', border: 'border-blue-200', hover: 'hover:bg-blue-700', ring: 'ring-blue-100', chip: 'bg-blue-100 text-blue-700' },
  orange: { text: 'text-orange-600', bg: 'bg-orange-600', bgLight: 'bg-orange-50', border: 'border-orange-200', hover: 'hover:bg-orange-700', ring: 'ring-orange-100', chip: 'bg-orange-100 text-orange-700' },
  purple: { text: 'text-purple-600', bg: 'bg-purple-600', bgLight: 'bg-purple-50', border: 'border-purple-200', hover: 'hover:bg-purple-700', ring: 'ring-purple-100', chip: 'bg-purple-100 text-purple-700' },
  teal: { text: 'text-teal-600', bg: 'bg-teal-600', bgLight: 'bg-teal-50', border: 'border-teal-200', hover: 'hover:bg-teal-700', ring: 'ring-teal-100', chip: 'bg-teal-100 text-teal-700' },
};

const InsurancePage = () => {
  const { slug } = useParams();
  const product = insuranceProducts[slug];
  const [openFaq, setOpenFaq] = useState(0);
  const [form, setForm] = useState({ name: '', phone: '', city: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useSeo({
    title: product ? `${product.name} | Compare & Buy Online | InsureRiskFree Solutions` : 'Insurance | InsureRiskFree Solutions',
    description: product
      ? `Compare ${product.name.toLowerCase()} insurance plans online in India. Get fast quotes, expert advice, and buy the best ${product.name.toLowerCase()} coverage with trusted claim support.`
      : 'Compare insurance plans online in India. Get fast quotes, expert advice, and trusted claim support.',
    canonical: product ? `https://insureriskfree.com/insurance/${slug}` : 'https://insureriskfree.com/',
    url: product ? `https://insureriskfree.com/insurance/${slug}` : 'https://insureriskfree.com/',
    ogImage: 'https://insureriskfree.com/og-image.jpg',
    twitterImage: 'https://insureriskfree.com/og-image.jpg',
    keywords: product
      ? `${product.name.toLowerCase()} insurance, compare ${product.name.toLowerCase()} insurance, buy ${product.name.toLowerCase()} insurance online, ${product.name.toLowerCase()} quotes`
      : 'insurance comparison India, buy insurance online, insurance quote',
  });

  if (!product) return <Navigate to="/" replace />;

  const a = accentClasses[product.accent] || accentClasses.emerald;
  const HeroIcon = iconMap[product.icon] || Shield;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || form.phone.length !== 10) return;
    setSubmitting(true);
    await submitLead({
      type: product.name,
      name: form.name,
      phone: form.phone,
      city: form.city,
    });
    setSubmitting(false);
    setSubmitted(true);
    setTimeout(() => { setSubmitted(false); setForm({ name: '', phone: '', city: '' }); }, 5000);
  };

  const otherProducts = Object.values(insuranceProducts).filter((p) => p.slug !== slug);

  return (
    <div className="bg-white">
      <StructuredData
        product={product}
        breadcrumbs={[
          { name: 'Home', url: 'https://insureriskfree.com/' },
          { name: 'Insurance', url: 'https://insureriskfree.com/insurance' },
          { name: product.name, url: `https://insureriskfree.com/insurance/${slug}` },
        ]}
      />
      {/* Breadcrumb */}
      <div className="border-b border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-sm">
          <nav className="flex items-center gap-2 text-slate-500">
            <Link to="/" className="hover:text-[#00B48E]">Home</Link>
            <span>/</span>
            <span className="text-slate-700">Insurance</span>
            <span>/</span>
            <span className={`font-medium ${a.text}`}>{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className={`relative overflow-hidden bg-gradient-to-br ${product.heroGradient}`}>
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className={`absolute top-10 -left-16 w-72 h-72 rounded-full ${a.bgLight} blur-3xl`} />
          <div className={`absolute bottom-10 -right-20 w-96 h-96 rounded-full ${a.bgLight} blur-3xl`} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className={`inline-flex items-center gap-2 ${a.chip} px-4 py-1.5 rounded-full text-sm font-medium mb-5`}>
                <HeroIcon className="w-4 h-4" /> {product.name}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F2A4B] leading-[1.1] tracking-tight">
                {product.tagline}
              </h1>
              <p className="mt-5 text-lg text-slate-600 max-w-lg leading-relaxed">
                {product.description}
              </p>

              <div className="mt-7 flex flex-wrap gap-5">
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Starting from</div>
                  <div className="text-2xl font-bold text-[#0F2A4B]">₹ {product.startingPrice}</div>
                </div>
                <div className="h-12 w-px bg-slate-200" />
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Cover up to</div>
                  <div className="text-2xl font-bold text-[#0F2A4B]">{product.coverUpTo}</div>
                </div>
              </div>

              <div className="mt-7 flex flex-wrap gap-3">
                <Button className={`${a.bg} ${a.hover} text-white rounded-full px-7 py-6 text-base font-semibold shadow-md`}>
                  Get Free Quote <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <a href="tel:+916364849979">
                  <Button variant="outline" className="rounded-full px-7 py-6 text-base font-semibold border-slate-300 text-slate-700 hover:bg-slate-50">
                    <Phone className="w-4 h-4 mr-2" /> Talk to Expert
                  </Button>
                </a>
              </div>
            </div>

            {/* Quote form */}
            <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-5">
                <div className={`w-11 h-11 rounded-xl ${a.bgLight} ${a.text} flex items-center justify-center`}>
                  <HeroIcon className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-bold text-[#0F2A4B]">Get {product.name} Quote</div>
                  <div className="text-xs text-slate-500">Our expert will call you in 5 minutes</div>
                </div>
              </div>

              {submitted ? (
                <div className="py-10 text-center">
                  <div className={`w-16 h-16 rounded-full ${a.bgLight} flex items-center justify-center mx-auto mb-4`}>
                    <CheckCircle2 className={`w-9 h-9 ${a.text}`} />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F2A4B]">Thanks, {form.name.split(' ')[0] || 'friend'}!</h3>
                  <p className="text-slate-600 text-sm mt-1">Our expert will reach out to you shortly on +91 {form.phone}.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3">
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your full name"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-[#00B48E]"
                    required
                  />
                  <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden focus-within:border-[#00B48E]">
                    <span className="px-3 py-3 bg-slate-50 text-slate-700 text-sm font-medium border-r">+91</span>
                    <input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                      placeholder="10-digit mobile number"
                      className="flex-1 px-3 py-3 text-sm outline-none"
                      required
                    />
                  </div>
                  <input
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="City (optional)"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-[#00B48E]"
                  />
                  <Button type="submit" disabled={submitting} className={`w-full ${a.bg} ${a.hover} text-white rounded-xl py-6 font-semibold disabled:opacity-60`}>
                    {submitting ? 'Submitting...' : 'View Best Quotes'}
                  </Button>
                  <p className="text-[11px] text-slate-500 text-center pt-1">By submitting, you agree to receive calls from our insurance experts.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F2A4B]">Why choose our {product.name}?</h2>
            <p className="mt-3 text-slate-600 max-w-2xl mx-auto">Compare, customise, and claim — all with the support of RiskFree expert advisors.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {product.benefits.map((b) => {
              const Icon = iconMap[b.icon] || Shield;
              return (
                <div key={b.title} className="group bg-white rounded-2xl border border-slate-100 p-6 hover:-translate-y-1 hover:shadow-lg hover:border-emerald-200 transition-all">
                  <div className={`w-12 h-12 rounded-xl ${a.bgLight} ${a.text} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0F2A4B] mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F2A4B]">What’s covered?</h2>
            <p className="mt-3 text-slate-600">Comprehensive protection across all critical scenarios — from small day-to-day issues to major emergencies.</p>
            <div className="mt-7 flex items-center gap-3 p-5 rounded-2xl bg-white border border-slate-100">
              <div className={`w-12 h-12 rounded-xl ${a.bgLight} ${a.text} flex items-center justify-center`}>
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <div className="font-semibold text-[#0F2A4B]">IRDAI Licensed Advisor</div>
                <div className="text-xs text-slate-500">Unbiased comparison across 20+ insurers</div>
              </div>
            </div>
          </div>
          <ul className="bg-white rounded-2xl border border-slate-100 p-3 divide-y divide-slate-100">
            {product.coverage.map((c) => (
              <li key={c} className="flex items-start gap-3 p-4">
                <CheckCircle2 className={`w-5 h-5 ${a.text} shrink-0 mt-0.5`} />
                <span className="text-sm text-slate-700">{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQs */}
      {/* In-depth content for SEO / long-tail keywords */}
      {product.longContent && (
        <section className="py-12 lg:py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {product.longContent.map((para, idx) => (
              <p key={idx} className="mt-4 text-base text-slate-700 leading-relaxed">
                {para}
              </p>
            ))}
          </div>
        </section>
      )}

      {/* FAQs */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F2A4B]">Frequently Asked Questions</h2>
            <p className="mt-3 text-slate-600">Everything you need to know about {product.name.toLowerCase()}.</p>
          </div>
          <div className="space-y-3">
            {product.faqs.map((f, i) => (
              <div key={i} className="bg-white border border-slate-100 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between text-left p-5 hover:bg-slate-50 transition-colors"
                >
                  <span className="font-semibold text-[#0F2A4B] pr-4">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other products */}
      <section className="py-14 bg-slate-50 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl sm:text-2xl font-bold text-[#0F2A4B]">Explore other insurance products</h3>
            <Link to="/" className="text-sm font-semibold text-[#00B48E] hover:underline">Back to home →</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {otherProducts.map((p) => {
              const Icon = iconMap[p.icon] || Shield;
              return (
                <Link key={p.slug} to={`/insurance/${p.slug}`} className="group bg-white rounded-2xl border border-slate-100 p-5 hover:-translate-y-0.5 hover:shadow-md transition-all">
                  <div className={`w-11 h-11 rounded-xl ${accentClasses[p.accent].bgLight} ${accentClasses[p.accent].text} flex items-center justify-center mb-3`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="font-semibold text-[#0F2A4B]">{p.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">From ₹ {p.startingPrice}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#00B48E] group-hover:gap-2 transition-all">
                    Explore <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsurancePage;
